import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Package, Star, Users, TrendingUp, Eye, Zap } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface Stats {
  total_figurines: number;
  published_figurines: number;
  featured_figurines: number;
  total_ratings: number;
  total_users: number;
  admin_users: number;
  avg_global_rating: number | null;
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from('admin_stats').select('*').single().then(({ data }) => {
      if (data) setStats(data as Stats);
      setLoading(false);
    });
  }, []);

  const cards = [
    { label: 'Wszystkie figurki', value: stats?.total_figurines ?? 0, sub: `${stats?.published_figurines ?? 0} opublikowanych`, icon: Package, color: 'var(--primary)' },
    { label: 'Wyróżnione', value: stats?.featured_figurines ?? 0, sub: 'featured na stronie', icon: Zap, color: 'var(--chart-4)' },
    { label: 'Oceny', value: stats?.total_ratings ?? 0, sub: stats?.avg_global_rating ? `Śr. ${Number(stats.avg_global_rating).toFixed(2)} ★` : 'Brak ocen', icon: Star, color: 'var(--chart-4)' },
    { label: 'Użytkownicy', value: stats?.total_users ?? 0, sub: `${stats?.admin_users ?? 0} adminów`, icon: Users, color: 'var(--accent)' },
  ];

  return (
    <div className="p-8 space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-black text-foreground" style={{ fontFamily: 'Orbitron, sans-serif' }}>
          DASHBOARD
        </h1>
        <p className="text-muted-foreground mt-1" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
          Przegląd statystyk sklepu SOKAO-TOYS
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
        {cards.map((card, i) => (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08, type: 'spring', stiffness: 280, damping: 30 }}
            className="rounded-2xl border p-6"
            style={{
              background: 'oklch(0.16 0.03 280)',
              borderColor: 'oklch(0.25 0.045 280)',
              boxShadow: `0 4px 24px -8px ${card.color}22`,
            }}
          >
            <div className="flex items-start justify-between mb-4">
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center"
                style={{ background: `${card.color}18`, color: card.color, border: `1px solid ${card.color}35` }}
              >
                <card.icon className="w-5 h-5" />
              </div>
              <TrendingUp className="w-4 h-4 text-muted-foreground/40" />
            </div>
            {loading ? (
              <div className="h-8 w-16 rounded-lg animate-pulse" style={{ background: 'var(--muted)' }} />
            ) : (
              <div className="text-3xl font-black" style={{ fontFamily: 'Orbitron, sans-serif', color: card.color }}>
                {card.value}
              </div>
            )}
            <div className="text-sm font-semibold text-foreground mt-1" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
              {card.label}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {card.sub}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Quick actions */}
      <div>
        <h2 className="text-sm font-bold tracking-widest text-muted-foreground mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          SZYBKIE AKCJE
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: 'Dodaj nową figurkę', desc: 'Utwórz wpis i ustaw cenę', color: 'var(--primary)', section: 'figurines' },
            { label: 'Zarządzaj kategoriami', desc: 'Edytuj lub dodaj kategorię', color: 'var(--chart-4)', section: 'categories' },
            { label: 'Przeglądaj użytkowników', desc: 'Zmień role i uprawnienia', color: 'var(--accent)', section: 'users' },
          ].map((a) => (
            <motion.div
              key={a.label}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="rounded-xl border p-5 cursor-pointer group"
              style={{
                background: 'oklch(0.14 0.028 280)',
                borderColor: 'oklch(0.22 0.04 280)',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = a.color; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = 'oklch(0.22 0.04 280)'; }}
            >
              <Eye className="w-5 h-5 mb-3" style={{ color: a.color }} />
              <div className="font-bold text-sm text-foreground" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                {a.label}
              </div>
              <div className="text-xs text-muted-foreground mt-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {a.desc}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
